import MDBox from "../../../components/MDBox";
import {observer} from "mobx-react";
import DashboardProjectManagerLayout from "../../../components/DashboardProjectManager";
import {ROUTES} from "../../../services/constants";
import {Box, Card, CircularProgress} from "@mui/material";
import pxToRem from "../../../assets/theme/functions/pxToRem";
import {Form, Formik} from "formik";
import {projectManagerInitialValues, projectManagerValidationRules} from "../../admin/create-user-account/utils";
import * as Yup from "yup";
import FormikInput from "../../../components/FormikInput";
import MDButton from "../../../components/MDButton";
import UserAvatar from "assets/images/blank_avatar.png";
import camera from "assets/icons/camera.svg";
import { useEffect, useState } from "react";
import {showMessage, useApi, useIsMobile} from "../../../services/helpers";
import stripe_logo from "assets/images/stripe.png";
import MDTypography from "../../../components/MDTypography";

let validationSchema = {...projectManagerValidationRules}
delete validationSchema.permission_type

const MyAccount = () => {
  const api = useApi()
  const [loading, setLoading] = useState(true)
  const [initialValues, setInitialValues] = useState(projectManagerInitialValues)
  const [imgPreview, setImgPreview] = useState(UserAvatar);
  const isMobile = useIsMobile();

  // Stripe state for supply managers
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeAccountVerified, setStripeAccountVerified] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  // Detect redirect back from Stripe onboarding (?success=true or ?refresh=true)
  const searchParams = new URLSearchParams(window.location.search);
  const returnedFromStripe = searchParams.get('success') === 'true' || searchParams.get('refresh') === 'true';

  const handleUploadFile = (file) => {
    setImgPreview(URL.createObjectURL(file))
  }

  const getMyProfile = async () => {
    try {
      const response = await api.getMyProfile();
      if (response.kind === "ok") {
        setInitialValues(response.data)
        if (response.data.profile_picture) {
          setImgPreview(response.data.profile_picture)
        }
        if (response.data.is_supplymanager) {
          const alreadyVerified = response.data.stripe_account_verified === true;
          setStripeAccountVerified(alreadyVerified);

          if (alreadyVerified) {
            // Verified in DB — trust DB, no Stripe API call needed
            setStripeConnected(response.data.stripe_onboarding_complete === true);
            if (returnedFromStripe) {
              window.history.replaceState({}, '', window.location.pathname);
            }
          } else if (response.data.has_stripe_account) {
            // Account exists in Stripe but not yet verified in DB.
            // Call live API once to sync — handles return-from-onboarding even
            // when ?success=true URL param is missing (e.g. staging redirect issue).
            try {
              const stripeStatus = await api.getStripeAccountStatus();
              if (stripeStatus.kind === "ok") {
                const verified = stripeStatus.data.onboarding_status === "verified";
                setStripeConnected(verified);
                setStripeAccountVerified(verified);
              } else {
                setStripeConnected(false);
              }
            } catch {
              setStripeConnected(false);
            }
            if (returnedFromStripe) {
              window.history.replaceState({}, '', window.location.pathname);
            }
          } else {
            // No Stripe account at all — not connected
            setStripeConnected(false);
          }
        }
      }
    } catch {
      showMessage('Error getting profile')
    } finally {
      setLoading(false)
    }
  }

  const saveProfile = (data) => {
    setLoading(true);
    const keys = []
    if (initialValues.profile_picture !== data.profile_picture) {
      keys.push(['profile_picture'])
    }
    api.saveMyProfile(data, keys).then((result) => {
      if (result.kind === 'ok') {
        showMessage('Profile updated successfully', 'success')
      } else {
        showMessage()
      }
    })
      .catch(err => showMessage())
      .finally(() => { setLoading(false) })
  }

  const handleConnectStripe = async () => {
    setStripeLoading(true);
    try {
      const result = await api.manageConnectAccount();
      if (result.kind === "ok") {
        if (result.data?.onboarding_url) {
          // No account yet or onboarding incomplete — redirect to Stripe
          window.location.href = result.data.onboarding_url;
        } else {
          // Verified account toggled to connected
          setStripeConnected(true);
          showMessage("Stripe payments reconnected", "success");
        }
      } else {
        showMessage("Failed to connect Stripe", "error");
      }
    } catch (e) {
      showMessage("Something went wrong", "error");
    } finally {
      setStripeLoading(false);
    }
  };

  const handleDisconnectStripe = async () => {
    setStripeLoading(true);
    try {
      const result = await api.manageConnectAccount();
      if (result.kind === "ok") {
        setStripeConnected(false);
        showMessage("Stripe payments disconnected", "success");
      } else {
        showMessage("Failed to disconnect Stripe", "error");
      }
    } catch (e) {
      showMessage("Something went wrong", "error");
    } finally {
      setStripeLoading(false);
    }
  };

  useEffect(() => {
    getMyProfile()
  }, [])

  const renderStripeSection = () => {
    if (!initialValues.is_supplymanager) return null;

    const isConnected = stripeConnected;

    return (
      <MDBox
        sx={{
          width: '100%',
          mt: pxToRem(20),
          p: `${pxToRem(14)} ${pxToRem(16)}`,
          borderRadius: pxToRem(10),
          bgcolor: '#f8f9fa',
          border: '1px solid #e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: pxToRem(12),
        }}
      >
        {/* Logo + text */}
        <MDBox display="flex" alignItems="center" gap={pxToRem(12)}>
          <MDBox
            sx={{
              width: pxToRem(42),
              height: pxToRem(42),
              borderRadius: '50%',
              bgcolor: '#6772e5',
              flexShrink: 0,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={stripe_logo}
              alt="Stripe"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </MDBox>

          <MDBox>
            <MDTypography variant="body2" fontWeight="bold" sx={{ color: '#344767', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
              Stripe payments
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: isConnected ? '#4CAF50' : '#f44335', fontWeight: 600 }}>
              {isConnected ? 'Connected' : 'Not connected'}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Action button */}
        <MDButton
          variant="outlined"
          disabled={stripeLoading}
          onClick={isConnected ? handleDisconnectStripe : handleConnectStripe}
          sx={{
            color: '#344767',
            borderColor: '#dee2e6',
            fontWeight: 500,
            flexShrink: 0,
            minWidth: pxToRem(110),
            bgcolor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: isConnected ? 'rgba(0,0,0,0.03)' : '#4CAF50',
              borderColor: isConnected ? '#344767' : '#4CAF50',
              color: isConnected ? '#344767' : '#fff',
            },
          }}
        >
          {stripeLoading
            ? <CircularProgress size={14} color="inherit" />
            : isConnected ? 'Disconnect' : 'Connect'}
        </MDButton>
      </MDBox>
    );
  };

  return (
    <DashboardProjectManagerLayout
      title={'My Account'}
      backButton
      backRoute={ROUTES.USER_PROJECT_SELECTOR}
      alignContent={"center"}
    >
      <Card sx={{ width: '100%', maxWidth: pxToRem(860), p: pxToRem(52), display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Formik
          initialValues={initialValues}
          validationSchema={Yup.object().shape(validationSchema)}
          onSubmit={values => saveProfile(values)}
          enableReinitialize
        >
          {(formik) => (
            <Form style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: pxToRem(500), gap: 5}}>
              <MDBox sx={{ position: 'relative', mb: 1 }}>
                <Box
                  component={"img"}
                  src={imgPreview}
                  alt={"Profile Picture"}
                  sx={{
                    width: pxToRem(110),
                    height: pxToRem(110),
                    borderRadius: '50%',
                    objectFit: 'cover',
                    display: 'block',
                    bgcolor: '#e9ecef',
                  }}
                />
                <input
                  type='file'
                  accept='image/*'
                  id={'input_file'}
                  onChange={(e) => {
                    const file = e.target.files[0]
                    handleUploadFile(file);
                    formik.setFieldValue('profile_picture', file)
                  }}
                  style={{display: 'none'}}
                />
                <label htmlFor='input_file' style={{ cursor: 'pointer' }}>
                  <MDBox
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      width: pxToRem(28),
                      height: pxToRem(28),
                      borderRadius: '50%',
                      bgcolor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box component={"img"} src={camera} alt={"change photo"} sx={{ width: pxToRem(16), height: pxToRem(16) }} />
                  </MDBox>
                </label>
              </MDBox>
              <FormikInput type={"text"} label={"Full Name"} name={"name"} sx={{ width: "100%", mt:2 }}/>
              <FormikInput type={"text"} label={"Email"} name={"email"} sx={{ width: "100%", mt:2 }} InputProps={{ readOnly: true}} />
              <FormikInput type={"text"} label={"Job Title"} name={"title"} sx={{ width: "100%", mt:2 }}/>
              <FormikInput type={"phone_input"} label={"Phone Number"} name={"phone_number"} sx={{ width: "100%", mt:2 }}/>

              {initialValues.is_projectmanager === true && (<>
                <FormikInput type={"text"} label={"Company"} name={"company_name"} sx={{ width: "100%", mt:2 }} disabled />
                <FormikInput type={"text"} label={"Region"} name={"region_name"} sx={{ width: "100%", mt:2 }} disabled />
              </>)}
              {initialValues.is_supplymanager === true && (<>
                <FormikInput type={"text"} label={"Manufacturer"} name={"manufacturer_name"} sx={{ width: "100%", mt:2 }} disabled />
              </>)}

              {renderStripeSection()}

              <MDButton type={"submit"} color={"primary"} sx={{ width: pxToRem(288), mt: pxToRem(20) }} loading={loading} disabled={loading}>
                Save
              </MDButton>
            </Form>
          )}
        </Formik>
      </Card>
    </DashboardProjectManagerLayout>
  );
}

export default observer(MyAccount);
